import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { AccountIntent, useActivation } from '../ActivationContext';
import { useActivationBase } from './useActivationBase';

import { buildRedirectParam } from '@/utils/urlHelpers';

/**
 * Returns a function that navigates to the correct post-auth step based on account intent.
 *   Create intent → /coverage (employer coverage form)
 *   Login intent  → /complete (session handoff)
 *
 * All auth entry points (OTP, email magic link, email+password, access code) should
 * use this hook so the routing logic stays in one place.
 *
 * @param redirectToOverride - use instead of state.redirectTo (e.g. when state hasn't been
 *   populated yet, such as in ephemeral-session callbacks where sessionStorage is unavailable)
 */
const usePostAuthNavigation = (redirectToOverride?: string) => {
  const { state, dispatch } = useActivation();
  const history = useHistory();
  const base = useActivationBase();

  return useCallback((options?: { replace?: boolean }) => {
    const redirectParam = buildRedirectParam(redirectToOverride ?? state.redirectTo);
    const navigate = options?.replace ? history.replace : history.push;

    if (state.accountIntent === AccountIntent.Create) {
      dispatch({ type: 'SET_STEP', step: 'coverage_form' });
      navigate(`${base}/coverage${redirectParam}`);
    } else {
      dispatch({ type: 'SET_STEP', step: 'activation_complete' });
      navigate(`${base}/complete${redirectParam}`);
    }
  }, [state.accountIntent, state.redirectTo, redirectToOverride, dispatch, history, base]);
};

export default usePostAuthNavigation;
